/**
 * @fileoverview CTA Section - Call to action section
 *
 * Purpose: Final conversion section with headline and CTA
 * Features:
 * - Large headline
 * - Value proposition text
 * - Primary CTA button to open demo request dialog
 *
 * Used by: Homepage (index.tsx)
 */

import { useState } from "react";
import { Button } from "@/ui/primitives";
import { ArrowRight } from "lucide-react";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";
import { usePostHog } from "@/utils/posthog";

/**
 * Call-to-action section
 *
 * Centered headline above a primary "Book a demo" button that opens the
 * demo request dialog.
 *
 * Theming: uses the global semantic CSS variables so the section adapts to
 * light and dark mode (the marketing footer exposes a theme switcher).
 */
export function CTASection() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);
  const posthog = usePostHog();

  const handleDemoRequest = () => {
    posthog?.capture("demo_requested", {
      button_location: "cta_section",
      button_text: "Book a demo",
    });
    setIsDemoDialogOpen(true);
  };

  return (
    <>
      <section className="relative z-10 w-full border-t border-primary/10 bg-background py-8 text-foreground md:py-24">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-10 px-6 text-center">
          <h2 className="text-2xl font-medium leading-[1.2] md:text-[42px]">
            Join the ambitious firms scaling their capacity{" "}
            <br className="hidden md:inline" />
            &amp; maximising creditor returns with Morgan.
          </h2>

          <Button
            variant="default"
            size="lg"
            expandIcon
            className="gap-2 text-base"
            onClick={handleDemoRequest}
          >
            Book a demo
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </>
  );
}
